// =============================================
// APLICAR ABONO - DISTRIBUCIÓN AUTOMÁTICA
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import paymentService from '../../services/paymentService';
import userService from '../../services/userService';

const DepositForm = ({ open, onClose, onSuccess }) => {
  // FORM: Estados
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [pendingMonths, setPendingMonths] = useState([]);
  const [preview, setPreview] = useState(null);

  const [formData, setFormData] = useState({
    user_id: '',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const steps = ['Datos del Abono', 'Vista Previa', 'Confirmar'];

  // FORM: Cargar usuarios al abrir
  useEffect(() => {
    if (open) {
      loadUsers();
      resetForm();
    }
  }, [open]);

  // FORM: Cargar propietarios
  const loadUsers = async () => {
    try {
      const response = await userService.getAll();
      const owners = response.users?.filter(u => u.role === 'owner') || [];
      setUsers(owners);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
    }
  };

  // FORM: Resetear formulario
  const resetForm = () => {
    setFormData({
      user_id: '',
      amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      notes: '',
    });
    setActiveStep(0);
    setPendingMonths([]);
    setPreview(null);
    setError('');
  };

  // FORM: Manejar cambios
  const handleChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  // FORM: Cargar meses pendientes al seleccionar propietario
  const handleUserChange = async (userId) => {
    // Convertir a número inmediatamente
    const userIdNum = parseInt(userId);

    setFormData({ ...formData, user_id: userIdNum });
    setPendingMonths([]);

    if (!userIdNum || isNaN(userIdNum)) {
      return;
    }

    setLoadingPreview(true);
    setError('');

    try {
      console.log('Cargando meses pendientes para user_id:', userIdNum);
      console.log('Tipo de user_id:', typeof userIdNum);
      console.log('URL que se construirá:', `/payments/pending/${userIdNum}`);

      const response = await paymentService.getPendingMonths(userIdNum);
      console.log('Respuesta del servidor:', response);

      setPendingMonths(response.pending_months || []);

      if (response.pending_months && response.pending_months.length > 0) {
        console.log('Meses pendientes encontrados:', response.pending_months.length);
      } else {
        console.log('No se encontraron meses pendientes');
      }
    } catch (err) {
      console.error('Error al cargar meses pendientes:', err);
      console.error('Error completo:', err.response);
      setError('Error al cargar información de deuda');
    } finally {
      setLoadingPreview(false);
    }
  };

  // FORM: Generar vista previa
  const handleGeneratePreview = () => {
    // Validar
    if (!formData.user_id || !formData.amount || formData.amount <= 0) {
      setError('Completa todos los campos requeridos');
      return;
    }

    if (pendingMonths.length === 0) {
      setError('El propietario no tiene meses pendientes');
      return;
    }

    // Simular distribución
    let remaining = parseFloat(formData.amount);
    const distribution = [];

    for (const month of pendingMonths) {
      if (remaining <= 0.01) break;

      const amountToApply = Math.min(remaining, month.pending);
      const newPaid = month.paid + amountToApply;
      const monthlyFee = month.monthly_fee;
      const isComplete = (monthlyFee - newPaid) < 0.01;

      distribution.push({
        year: month.year,
        month: month.month,
        monthly_fee: monthlyFee,
        previous_paid: month.paid,
        amount_to_apply: amountToApply,
        new_paid: newPaid,
        pending_after: monthlyFee - newPaid,
        status: isComplete ? 'completo' : 'parcial'
      });

      remaining -= amountToApply;
    }

    // SI AÚN QUEDA DINERO: Simular aplicación a meses futuros (Prepagos)
    if (remaining > 0.01) {
      let nextYear, nextMonth;

      if (pendingMonths.length > 0) {
        const last = pendingMonths[pendingMonths.length - 1];
        nextYear = last.year;
        nextMonth = last.month;
      } else {
        nextYear = new Date().getFullYear();
        nextMonth = new Date().getMonth() + 1;
      }

      // Avanzar al siguiente mes
      nextMonth++;
      if (nextMonth > 12) {
        nextMonth = 1;
        nextYear++;
      }

      // Loop para simular prepagos
      while (remaining > 0.01) {
        // Asumimos cuota estándar para futuros (podría variar si hay deuda parcial futura, pero asumimos limpio)
        // En simulación frontend no consultamos BD por futuros pagos, asumimos 0 pagado.
        const monthlyFee = pendingMonths.length > 0 ? pendingMonths[0].monthly_fee : 15.00; // Fallback a 15 si no hay info
        const alreadyPaid = 0;
        const pending = monthlyFee; // Asumimos todo pendiente

        const amountToApply = Math.min(remaining, pending);
        const newPaid = alreadyPaid + amountToApply;
        const isComplete = (monthlyFee - newPaid) < 0.01;

        distribution.push({
          year: nextYear,
          month: nextMonth,
          monthly_fee: monthlyFee,
          previous_paid: alreadyPaid,
          amount_to_apply: amountToApply,
          new_paid: newPaid,
          pending_after: monthlyFee - newPaid,
          status: isComplete ? 'completo' : 'parcial (adelanto)'
        });

        remaining -= amountToApply;

        // Avanzar mes
        nextMonth++;
        if (nextMonth > 12) {
          nextMonth = 1;
          nextYear++;
        }

        // Safety break
        if (nextYear > new Date().getFullYear() + 10) break;
      }
    }

    setPreview({
      distribution: distribution,
      total_deposited: parseFloat(formData.amount),
      total_applied: parseFloat(formData.amount) - remaining,
      remaining_credit: remaining,
      complete_months: distribution.filter(d => d.status === 'completo').length,
      partial_months: distribution.filter(d => d.status === 'parcial').length,
    });

    setError('');
    setActiveStep(1);
  };

  // FORM: Aplicar abono
  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await paymentService.applyDeposit(formData);
      onSuccess(response.summary);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al aplicar abono');
      setActiveStep(1); // Volver a preview
    } finally {
      setLoading(false);
    }
  };

  // HELPERS: Obtener nombre del mes
  const getMonthName = (month) => {
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return months[month - 1];
  };

  // HELPERS: Formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PA', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon />
          Aplicar Abono Automático
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* PASO 1: DATOS DEL ABONO */}
        {activeStep === 0 && (
          <Box>
            <Grid container spacing={2}>
              {/* Propietario */}
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Propietario</InputLabel>
                  <Select
                    value={formData.user_id}
                    label="Propietario"
                    onChange={(e) => handleUserChange(e.target.value)}
                  >
                    {users.map((user) => (
                      <MenuItem key={user.id} value={parseInt(user.id)}>
                        {user.name} {user.house_number ? `- Casa ${user.house_number}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Monto */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Monto del Abono"
                  type="number"
                  value={formData.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  required
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>

              {/* Fecha */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Fecha de Pago"
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => handleChange('payment_date', e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Notas */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notas (opcional)"
                  multiline
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                />
              </Grid>
            </Grid>

            {/* Meses pendientes */}
            {loadingPreview ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <CircularProgress />
              </Box>
            ) : pendingMonths.length > 0 ? (
              <Box sx={{ mt: 3 }}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Deuda actual:</strong> {pendingMonths.length} meses pendientes = {formatCurrency(pendingMonths.reduce((sum, m) => sum + m.pending, 0))}
                  </Typography>
                </Alert>
              </Box>
            ) : formData.user_id ? (
              <Alert severity="success" sx={{ mt: 3 }}>
                Este propietario no tiene meses pendientes
              </Alert>
            ) : null}
          </Box>
        )}

        {/* PASO 2: VISTA PREVIA */}
        {activeStep === 1 && preview && (
          <Box>
            {/* Resumen */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    Monto a Abonar
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(preview.total_deposited)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    Meses Completos
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {preview.complete_months}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    Meses Parciales
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    {preview.partial_months}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Tabla de distribución */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Período</TableCell>
                    <TableCell align="right">Pagado Antes</TableCell>
                    <TableCell align="right">Se Aplicará</TableCell>
                    <TableCell align="right">Nuevo Total</TableCell>
                    <TableCell align="right">Pendiente</TableCell>
                    <TableCell>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.distribution.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {getMonthName(item.month)} {item.year}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.previous_paid)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold" color="primary">
                          +{formatCurrency(item.amount_to_apply)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.new_paid)}
                      </TableCell>
                      <TableCell align="right">
                        {item.pending_after > 0.01
                          ? formatCurrency(item.pending_after)
                          : '-'
                        }
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.status === 'completo' ? 'Completo' : 'Parcial'}
                          color={item.status === 'completo' ? 'success' : 'warning'}
                          size="small"
                          icon={item.status === 'completo' ? <CheckIcon /> : <WarningIcon />}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Crédito restante */}
            {preview.remaining_credit > 0.01 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Sobrará un crédito de {formatCurrency(preview.remaining_credit)} que no será aplicado
              </Alert>
            )}
          </Box>
        )}

        {/* PASO 3: CONFIRMAR */}
        {activeStep === 2 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              ¿Confirmar Aplicación de Abono?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Se crearán {preview?.distribution.length} pagos automáticamente
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>

        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={handleGeneratePreview}
            disabled={!formData.user_id || !formData.amount}
          >
            Ver Distribución
          </Button>
        )}

        {activeStep === 1 && (
          <>
            <Button onClick={() => setActiveStep(0)}>
              Atrás
            </Button>
            <Button
              variant="contained"
              onClick={() => setActiveStep(2)}
            >
              Continuar
            </Button>
          </>
        )}

        {activeStep === 2 && (
          <>
            <Button onClick={() => setActiveStep(1)}>
              Atrás
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Confirmar y Aplicar'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DepositForm;
